import type Phaser from 'phaser';
import type { MvpCharacterId } from '../session/game-session';
import { CHARACTER_CARD_DATA } from '../character-select/character-select-layout';
import { StickPreviewRenderer } from '../character-select/stick-preview-renderer';
import { RESULT_LAYOUT } from './result-layout';

export type ResultWinnerPlayer = 'p1' | 'p2' | 'cpu' | 'draw';

export interface ResultCardModel {
  readonly winnerCharacterId: MvpCharacterId | null;
  readonly winnerPlayer: ResultWinnerPlayer;
}

export class ResultCardComponent {
  public readonly container: Phaser.GameObjects.Container;

  public constructor(
    scene: Phaser.Scene,
    model: ResultCardModel,
  ) {
    const layout = RESULT_LAYOUT;
    const { winnerCharacterId, winnerPlayer } = model;
    const isDraw = winnerPlayer === 'draw' || winnerCharacterId === null;
    const cardData = winnerCharacterId !== null ? CHARACTER_CARD_DATA[winnerCharacterId] : null;

    this.container = scene.add.container(layout.card.centerX, layout.card.centerY);

    const width = layout.card.width;
    const height = layout.card.height;
    const halfW = width / 2;
    const halfH = height / 2;

    // Background & Border
    const bgGraphics = scene.add.graphics();
    let borderColor: number = layout.colors.cardBorder;
    let borderWidth = 1.5;
    let badgeText = '【 引き分け 】';
    let badgeColor = '#64748b';
    let badgeBgColor: number = 0x0f172a;
    let badgeBorderColor: number = 0x334155;
    let playerSlot: 'p1' | 'p2' | 'cpu' | 'none' = 'none';

    if (winnerPlayer === 'p1') {
      borderColor = layout.colors.p1Accent;
      borderWidth = 2.5;
      badgeText = '【 1P 勝利 】';
      badgeColor = '#38bdf8';
      badgeBgColor = 0x0c4a6e;
      badgeBorderColor = layout.colors.p1Accent;
      playerSlot = 'p1';
    } else if (winnerPlayer === 'p2') {
      borderColor = layout.colors.p2Accent;
      borderWidth = 2.5;
      badgeText = '【 2P 勝利 】';
      badgeColor = '#f97316';
      badgeBgColor = 0x431407;
      badgeBorderColor = layout.colors.p2Accent;
      playerSlot = 'p2';
    } else if (winnerPlayer === 'cpu') {
      borderColor = layout.colors.cpuAccent;
      borderWidth = 2;
      badgeText = '【 CPU 勝利 】';
      badgeColor = '#94a3b8';
      badgeBgColor = 0x1e293b;
      badgeBorderColor = 0x475569;
      playerSlot = 'cpu';
    }

    // Base card fill
    bgGraphics.fillStyle(0x0f172a, 0.94);
    bgGraphics.fillRoundedRect(-halfW, -halfH, width, height, 6);

    // Header strip tint
    const themeColor = cardData?.themeColor ?? 0x334155;
    bgGraphics.fillStyle(themeColor, 0.12);
    bgGraphics.fillRect(-halfW + 2, -halfH + 2, width - 4, 40);

    // Accent line at top
    bgGraphics.fillStyle(themeColor, 0.9);
    bgGraphics.fillRect(-halfW + 4, -halfH + 2, width - 8, 3.5);

    // Divider line between character header and preview
    bgGraphics.lineStyle(1, 0x1e293b, 0.8);
    bgGraphics.lineBetween(-halfW + 24, -26, halfW - 24, -26);

    // Outer border
    bgGraphics.lineStyle(borderWidth, borderColor, 1);
    bgGraphics.strokeRoundedRect(-halfW, -halfH, width, height, 6);

    // Badge
    const badgeW = Math.min(220, Math.max(140, badgeText.length * 15 + 30));
    const badgeH = 28;
    const badgeY = layout.cardOffsets.badge.y;
    const badgeBg = scene.add.graphics();
    badgeBg.fillStyle(badgeBgColor, 0.95);
    badgeBg.fillRoundedRect(-badgeW / 2, badgeY - badgeH / 2, badgeW, badgeH, 4);
    badgeBg.lineStyle(1.5, badgeBorderColor, 1);
    badgeBg.strokeRoundedRect(-badgeW / 2, badgeY - badgeH / 2, badgeW, badgeH, 4);

    const badgeLabel = scene.add.text(0, badgeY, badgeText, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '15px',
      fontStyle: 'bold',
      color: badgeColor,
      align: 'center',
    }).setOrigin(0.5);

    // WINNER Header
    const winnerTitle = scene.add.text(0, layout.cardOffsets.winnerLabel.y, isDraw ? 'DRAW' : 'WINNER', {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '30px',
      fontStyle: 'bold',
      color: isDraw ? '#94a3b8' : '#f8fafc',
      align: 'center',
    }).setOrigin(0.5);

    // Character Name
    const nameStr = cardData ? cardData.characterName : '引き分け';
    const nameText = scene.add.text(0, layout.cardOffsets.name.y, nameStr, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '24px',
      fontStyle: 'bold',
      color: cardData ? cardData.departmentColor : '#cbd5e1',
      align: 'center',
    }).setOrigin(0.5);

    // Department
    const deptStr = cardData ? cardData.department : '両者互角';
    const deptText = scene.add.text(0, layout.cardOffsets.department.y, deptStr, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '14px',
      color: '#94a3b8',
      align: 'center',
    }).setOrigin(0.5);

    // Stick Figure Victory Preview
    const previewGraphics = scene.add.graphics();
    if (winnerCharacterId !== null) {
      StickPreviewRenderer.draw(
        previewGraphics,
        winnerCharacterId,
        0,
        layout.cardOffsets.preview.y,
        { playerSlot, scale: 0.72, poseState: 'win' },
      );
    }

    this.container.add([
      bgGraphics,
      badgeBg,
      badgeLabel,
      winnerTitle,
      nameText,
      deptText,
      previewGraphics,
    ]);
  }
}
